import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { UserPlus, Users, Settings, Lock } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Available modules for assignment
const AVAILABLE_MODULES = [
  { id: "basic", name: "Basic Details" },
  { id: "design", name: "Design Documents" },
  { id: "documents", name: "Other Documents" },
  { id: "boq", name: "BOQ & Quotations" },
  { id: "legal", name: "Legal Documents" },
  { id: "timeline", name: "Timeline & Milestones" },
  { id: "payments", name: "Payments & Finance" },
  { id: "vendors", name: "Vendors" },
  { id: "materials", name: "Materials & Procurement" },
  { id: "progress", name: "Site Progress" },
  { id: "risks", name: "Risk Management" },
];

function InfoRow({ label, value }) {
  return (
    <div className="flex justify-between py-2">
      <span className="text-sm text-slate-500">{label}</span>
      <span className="text-sm font-medium text-slate-900">{value}</span>
    </div>
  );
}

export function BasicDetailsContent({ project, token, user, onProjectUpdate }) {
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [moduleAssignDialogOpen, setModuleAssignDialogOpen] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [assignedUserDetails, setAssignedUserDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUserForModules, setSelectedUserForModules] = useState(null);
  const [selectedModules, setSelectedModules] = useState([]);

  const isAdmin = user?.role === 'Admin';

  useEffect(() => {
    if (project?.assigned_users) {
      setSelectedUsers(project.assigned_users);
    }
    fetchAssignedUserDetails();
  }, [project]);

  const fetchAssignedUserDetails = async () => {
    if (!project?.id) return;
    try {
      const response = await axios.get(`${API}/projects/${project.id}/assigned-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssignedUserDetails(response.data);
    } catch (error) {
      console.error("Failed to fetch assigned users");
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await axios.get(`${API}/auth/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllUsers(response.data.filter(u => u.role !== 'Admin'));
    } catch (error) {
      console.error("Failed to fetch users");
    }
  };

  const openAssignDialog = () => {
    fetchAllUsers();
    setSelectedUsers(project.assigned_users || []);
    setAssignDialogOpen(true);
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSaveAssignment = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/projects/${project.id}/assign-users`, 
        { user_ids: selectedUsers },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Users assigned successfully");
      setAssignDialogOpen(false);
      fetchAssignedUserDetails();
      if (onProjectUpdate) onProjectUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign users");
    } finally {
      setLoading(false);
    }
  };

  const openModuleAssignDialog = (userDetail) => {
    setSelectedUserForModules(userDetail);
    setSelectedModules(userDetail.assigned_modules || []);
    setModuleAssignDialogOpen(true);
  };

  const toggleModuleSelection = (moduleId) => {
    setSelectedModules(prev => 
      prev.includes(moduleId) 
        ? prev.filter(id => id !== moduleId)
        : [...prev, moduleId]
    );
  };

  const handleSaveModuleAssignment = async () => {
    if (!selectedUserForModules) return;
    setLoading(true);
    try {
      await axios.put(`${API}/projects/${project.id}/assign-modules`, 
        { user_id: selectedUserForModules.id, modules: selectedModules },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Modules assigned to ${selectedUserForModules.name}`);
      setModuleAssignDialogOpen(false);
      fetchAssignedUserDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign modules");
    } finally {
      setLoading(false);
    }
  };

  const selectAllModules = () => {
    setSelectedModules(AVAILABLE_MODULES.map(m => m.id));
  };

  const deselectAllModules = () => {
    setSelectedModules([]);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <h4 className="font-semibold text-slate-900 border-b pb-2">Project Information</h4>
          <InfoRow label="Project Code" value={project.project_code || project.id} />
          <InfoRow label="Project Name" value={project.name} />
          <InfoRow label="Client" value={project.client} />
          <InfoRow label="Project Type" value={project.type} />
          <InfoRow label="Execution Model" value={project.execution_model} />
          <InfoRow label="Total Budget" value={project.total_budget ? `₹${project.total_budget.toLocaleString()}` : "Not set"} />
          <InfoRow label="Status" value={project.status} />
        </div>
        <div className="space-y-4">
          <h4 className="font-semibold text-slate-900 border-b pb-2">Location & Timeline</h4>
          <InfoRow label="Site Address" value={project.site_address || "Not specified"} />
          <InfoRow label="City" value={project.city} />
          <InfoRow label="State" value={project.state} />
          <InfoRow label="Area" value={project.area} />
          <InfoRow label="Start Date" value={project.start_date} />
          <InfoRow label="Target Date" value={project.target_date} />
          <InfoRow label="Project Manager" value={project.pm} />
        </div>
      </div>

      {/* Assigned Users Section */}
      <div className="border-t pt-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-slate-500" />
            <h4 className="font-semibold text-slate-900">Assigned Team Members & Module Access</h4>
          </div>
          {isAdmin && (
            <Button onClick={openAssignDialog} variant="outline" size="sm" data-testid="assign-users-btn">
              <UserPlus className="h-4 w-4 mr-2" />Assign Users
            </Button>
          )}
        </div>
        
        {assignedUserDetails.length > 0 ? (
          <div className="space-y-3">
            {assignedUserDetails.map(u => (
              <div key={u.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                    <span className="text-sm font-medium text-slate-600">{u.name?.charAt(0)?.toUpperCase()}</span>
                  </div>
                  <div>
                    <p className="font-medium text-sm text-slate-900">{u.name}</p>
                    <p className="text-xs text-slate-500">{u.email} • {u.role}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="text-xs text-slate-500">Modules Assigned</p>
                    <p className="text-sm font-medium text-slate-900">
                      {u.assigned_modules?.length || 0} / {AVAILABLE_MODULES.length}
                    </p>
                  </div>
                  {isAdmin && (
                    <Button 
                      onClick={() => openModuleAssignDialog(u)} 
                      variant="outline" 
                      size="sm"
                      data-testid={`assign-modules-${u.id}`}
                    >
                      <Settings className="h-4 w-4 mr-1" />Modules
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500 italic">No users assigned to this project yet.</p>
        )}
      </div>

      {/* Assign Users Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Users to Project</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {allUsers.length === 0 ? (
              <p className="text-sm text-slate-500">No users available to assign.</p>
            ) : (
              allUsers.map(u => (
                <div key={u.id} className="flex items-center space-x-3 p-2 hover:bg-slate-50 rounded">
                  <Checkbox
                    id={u.id}
                    checked={selectedUsers.includes(u.id)}
                    onCheckedChange={() => toggleUserSelection(u.id)}
                    data-testid={`user-checkbox-${u.id}`}
                  />
                  <label htmlFor={u.id} className="flex-1 cursor-pointer">
                    <p className="font-medium text-sm">{u.name}</p>
                    <p className="text-xs text-slate-500">{u.email} • {u.role}</p>
                  </label>
                </div>
              ))
            )}
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveAssignment} disabled={loading} data-testid="save-assignment-btn">
              {loading ? "Saving..." : "Save Assignment"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Modules Dialog */}
      <Dialog open={moduleAssignDialogOpen} onOpenChange={setModuleAssignDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Assign Modules to {selectedUserForModules?.name}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500 mb-4">
            Select which project modules this user can access. Unselected modules will appear locked.
          </p>
          <div className="flex gap-2 mb-4">
            <Button variant="outline" size="sm" onClick={selectAllModules}>Select All</Button>
            <Button variant="outline" size="sm" onClick={deselectAllModules}>Deselect All</Button>
          </div>
          <div className="grid grid-cols-2 gap-3 max-h-80 overflow-y-auto">
            {AVAILABLE_MODULES.map(module => (
              <div 
                key={module.id} 
                className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  selectedModules.includes(module.id) 
                    ? 'bg-blue-50 border-blue-300' 
                    : 'bg-slate-50 border-slate-200 hover:bg-slate-100'
                }`}
                onClick={() => toggleModuleSelection(module.id)}
              >
                <Checkbox
                  id={`module-${module.id}`}
                  checked={selectedModules.includes(module.id)}
                  onCheckedChange={() => toggleModuleSelection(module.id)}
                />
                <label htmlFor={`module-${module.id}`} className="cursor-pointer flex-1">
                  <p className="font-medium text-sm">{module.name}</p>
                </label>
                {!selectedModules.includes(module.id) && (
                  <Lock className="h-4 w-4 text-slate-400" />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between items-center pt-4 border-t">
            <p className="text-sm text-slate-500">
              {selectedModules.length} of {AVAILABLE_MODULES.length} modules selected
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setModuleAssignDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveModuleAssignment} disabled={loading}>
                {loading ? "Saving..." : "Save Modules"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
